import React, { useEffect, useState } from 'react';
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Space,
  message,
  Typography,
  Drawer,
} from 'antd';
import { ArrowLeftOutlined, CheckOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { userService, User, CreateUserData } from '../services/users';
import { teamService, Team, CreateTeamData } from '../services/teams';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';

const { Title } = Typography;

const statusOptions = [
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
];

export const UserFormPage: React.FC = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [form] = Form.useForm<CreateUserData>();
  const [submitting, setSubmitting] = useState(false);
  const [loadingUser, setLoadingUser] = useState(false);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [teamDrawerVisible, setTeamDrawerVisible] = useState(false);
  const [teamForm] = Form.useForm<CreateTeamData>();
  const { user } = useAuth();
  const canUpdate = hasPermission(user, 'settings.update');
  const canDelete = hasPermission(user, 'settings.delete');
  const navigate = useNavigate();

  useEffect(() => {
    loadTeams();
    if (isEdit && id) {
      loadUser(parseInt(id, 10));
    }
  }, [isEdit, id]);

  const loadTeams = async () => {
    setLoadingTeams(true);
    try {
      const response = await teamService.getAll();
      setTeams(response || []);
    } catch {
      message.error('Failed to load teams');
    } finally {
      setLoadingTeams(false);
    }
  };

  const loadUser = async (userId: number) => {
    setLoadingUser(true);
    try {
      const user = await userService.getById(userId);
      form.setFieldsValue({
        ...user,
        password: '', // Don't load password
      });
    } catch {
      message.error('Failed to load user');
      navigate('/users');
    } finally {
      setLoadingUser(false);
    }
  };

  const handleSubmit = async (values: CreateUserData) => {
    setSubmitting(true);
    try {
      const payload: any = { ...values };
      
      // In edit mode, if password is empty, don't send it (to keep current password)
      if (isEdit && (!payload.password || payload.password.trim() === '')) {
        delete payload.password;
      }

      if (isEdit && id) {
        await userService.update(parseInt(id, 10), payload);
        message.success('User updated successfully');
      } else {
        await userService.create(payload);
        message.success('User created successfully');
      }
      navigate('/users');
    } catch (error: any) {
      console.error('Failed to save user:', error);
      message.error(isEdit ? 'Failed to update user' : 'Failed to create user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateTeam = async (values: CreateTeamData) => {
    try {
      const newTeam = await teamService.create(values);
      message.success('Team created successfully');
      setTeamDrawerVisible(false);
      teamForm.resetFields();
      await loadTeams();
      form.setFieldValue('team_id', newTeam.id);
    } catch (error: any) {
      message.error('Failed to create team');
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: 24 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/users')}
          style={{ marginBottom: 16 }}
        >
          Back
        </Button>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={3} style={{ margin: 0 }}>
              {isEdit ? 'Edit User' : 'New User'}
            </Title>
            <div style={{ color: '#999', marginTop: 8 }}>
              Users / {isEdit ? `Edit User #${id}` : 'New User'}
            </div>
          </div>
          <Space>
            {isEdit && canDelete && (
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={() => {
                  if (window.confirm('Are you sure you want to delete this user?')) {
                    userService.delete(parseInt(id!, 10)).then(() => {
                      message.success('User deleted');
                      navigate('/users');
                    }).catch(() => message.error('Failed to delete user'));
                  }
                }}
              >
                Delete
              </Button>
            )}
            {isEdit && canUpdate && (
              <Button icon={<CheckOutlined />} onClick={() => form.submit()}>
                Update
              </Button>
            )}
          </Space>
        </div>
      </div>

      <Card bordered={false} bodyStyle={{ padding: 0 }} style={{ boxShadow: 'none' }}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          disabled={loadingUser || (isEdit && !canUpdate)}
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please enter name' }]}
          >
            <Input placeholder="Enter name" />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Please enter a valid email' },
            ]}
          >
            <Input placeholder="Enter email" />
          </Form.Item>

          <Form.Item
            name="password"
            label="Password"
            rules={[
              ...(isEdit ? [] : [{ required: true, message: 'Please enter password' }]),
              { min: 6, message: 'Password must be at least 6 characters' },
            ]}
          >
            <Input.Password
              placeholder={isEdit ? "Leave blank to keep current password" : "Enter password"}
            />
          </Form.Item>

          <Form.Item
            name="team_id"
            label="Team"
          >
            <Select
              placeholder="Select team"
              loading={loadingTeams}
              allowClear
              dropdownRender={(menu) => (
                <>
                  {menu}
                  <div style={{ padding: '8px', borderTop: '1px solid #f0f0f0' }}>
                    <Button
                      type="link"
                      onClick={() => setTeamDrawerVisible(true)}
                      style={{ width: '100%', textAlign: 'left' }}
                    >
                      Add Team
                    </Button>
                  </div>
                </>
              )}
            >
              {teams.map((team) => (
                <Select.Option key={team.id} value={team.id}>
                  {team.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="phone"
            label="Phone"
          >
            <Input placeholder="Enter phone number" />
          </Form.Item>

          <Form.Item
            name="department"
            label="Department"
          >
            <Input placeholder="Enter department" />
          </Form.Item>

          <Form.Item
            name="position"
            label="Position"
          >
            <Input placeholder="Enter position" />
          </Form.Item>

          <Form.Item
            name="status"
            label="Status"
            initialValue="active"
          >
            <Select placeholder="Select status">
              {statusOptions.map((option) => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <div style={{
            padding: '16px 24px',
            borderTop: '1px solid #f0f0f0',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            marginTop: 24,
          }}>
            <Space>
              <Button onClick={() => navigate('/users')}>Cancel</Button>
              {(!isEdit || canUpdate) && (
                <Button type="primary" htmlType="submit" loading={submitting} icon={<CheckOutlined />}>
                  {isEdit ? 'Update' : 'Create'}
                </Button>
              )}
            </Space>
          </div>
        </Form>
      </Card>

      <Drawer
        title="Add Team"
        placement="right"
        onClose={() => {
          setTeamDrawerVisible(false);
          teamForm.resetFields();
        }}
        open={teamDrawerVisible}
        width={400}
      >
        <Form
          form={teamForm}
          layout="vertical"
          onFinish={handleCreateTeam}
        >
          <Form.Item
            name="name"
            label="Team Name"
            rules={[
              { required: true, message: 'Please enter team name' },
              {
                validator: (_, value) => {
                  const name = (value || '').trim().toLowerCase();
                  if (!name) return Promise.resolve();
                  const exists = teams.some((team) => (team.name || '').trim().toLowerCase() === name);
                  return exists ? Promise.reject(new Error('Team name already exists')) : Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder="Enter team name" />
          </Form.Item>
          <Form.Item
            name="contact_number"
            label="Contact Number"
          >
            <Input placeholder="Enter contact number" />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[{ type: 'email', message: 'Please enter a valid email' }]}
          >
            <Input placeholder="Enter email" type="email" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Create
              </Button>
              <Button onClick={() => {
                setTeamDrawerVisible(false);
                teamForm.resetFields();
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
};
